/**
 * NewEventPopover - Popover de seleção da fonte ao criar um novo evento
 *
 * Apresenta uma grade de ícones (estilo "apps Google") com as fontes
 * suportadas pelo Calendário Global. Ao clicar em um tile, dispara
 * `onPick(source)`.
 *
 * RELEASE:
 *   2026-09-17 v2.1.0 - Tiles filtrados pelos módulos contratados (Tarefas e
 *                        Agendamento Online só aparecem quando habilitados).
 *   2026-06-26 v2.0.0 - Removidos tiles "Evento manual" e "Google Agenda":
 *                        o botão principal "Novo evento" agora cria evento
 *                        manual direto e o Google virou toggle dentro do modal.
 *   2026-06-25 v1.2.0 - Removido ícone "?" por tile (ajuda agora vive na
 *                        sidebar "Filtrar por fonte").
 *   2026-06-25 v1.1.0 - Removida opção "Mensagens Agendadas".
 *   2026-06-25 v1.0.0 - Versão inicial
 */

import { type ReactNode } from 'react';
// v2.1.0: flags de módulos contratados (CRM / Tarefas)
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import {
  CheckSquare,
  CalendarClock,
  type LucideIcon,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { SOURCE_LABELS } from './sourceMeta';

/** Tipo de fonte selecionável neste popover */
export type NewEventSource =
  | 'manual'
  | 'task'
  | 'google'
  | 'scheduled_message'
  | 'booking';

/** Definição visual de cada tile */
interface TileDef {
  source: NewEventSource;
  icon: LucideIcon;
  /** Classe do ícone (apenas cor, tokens semânticos) */
  iconClass: string;
}

// v2.0.0: apenas fontes que abrem outros fluxos (tarefa/agendamento)
const TILES: TileDef[] = [
  { source: 'task', icon: CheckSquare, iconClass: 'text-chart-2' },
  { source: 'booking', icon: CalendarClock, iconClass: 'text-chart-5' },
];


interface NewEventPopoverProps {
  /** Elemento que dispara o popover (geralmente o botão "Novo evento") */
  children: ReactNode;
  /** Quando true, o popover não abre (botão fica desabilitado pelo pai) */
  disabled?: boolean;
  /** Callback ao clicar em uma fonte */
  onPick: (source: NewEventSource) => void;
}

export function NewEventPopover({
  children,
  disabled,
  onPick,
}: NewEventPopoverProps) {
  // v2.1.0: só exibe tiles de módulos contratados pelo tenant.
  const { isCrmAvailable, isTasksAvailable } = useHeaderModuleFlags();
  const visibleTiles = TILES.filter((tile) => {
    if (tile.source === 'task') return isTasksAvailable;
    if (tile.source === 'booking') return isCrmAvailable;
    return true;
  });

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-56 p-3"
      >
        <div className="mb-2 px-1">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Criar novo
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-2">

          {visibleTiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.source}
                type="button"
                onClick={() => onPick(tile.source)}
                className={cn(
                  'group flex aspect-square flex-col items-center justify-center gap-1.5',
                  'rounded-lg border border-transparent bg-muted/30 px-2 py-2',
                  'transition-all hover:border-border hover:bg-muted/70',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                <Icon
                  className={cn(
                    'h-6 w-6 shrink-0 transition-transform group-hover:scale-110',
                    tile.iconClass,
                  )}
                />
                <span className="text-[10.5px] font-medium leading-tight text-foreground text-center line-clamp-2">
                  {SOURCE_LABELS[tile.source]}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
